import { childrenToString } from "../../internal/functions";
import { useTrackingId } from "../../hooks";
import { DataTrackingId, IanaZone } from "../../types";
import Popover from "../../internal/components/Popover";
import { Ref, useMemo, useRef } from "react";
import {
  MaskedDateInput,
  MaskedDateInputProps,
  MaskedDateInputRef,
} from "./internal/MaskedDateInput";
import { DateMode } from "./types";
import { useDateFieldSingleConversion } from "./internal/useDateFieldSingleConversion";
import {
  DateFieldSingleStateChangeHandler,
  useDateFieldSingleState,
} from "./internal/useDateFieldSingleState";
import { validateDate } from "./internal/utils";
import { useFocusWithin } from "../../internal/hooks";
import { DateFieldSingleCalendar } from "./internal/DateFieldSingleCalendar";
import { mergeRefs } from "../../hooks/useMergeRefs/useMergeRefs";

export type DateFieldSingleProps = Omit<
  MaskedDateInputProps,
  | "onChange"
  | "value"
  | "autoComplete"
  | "onClick"
  | "onKeyDown"
  | "ref"
  | "lastValidDate"
  | "setLastValidDate"
  | "required"
  | "onFocus"
  | "onBlur"
> & {
  value?: string | null;
  defaultValue?: string | null;
  onChange?: DateFieldSingleChangeHandler;
  onFocus?: (event: React.FocusEvent) => void;
  onBlur?: (event: React.FocusEvent) => void;
  mode?: Extract<DateMode, "mm/dd/yyyy" | "dd/mm/yyyy">;
  disableHint?: boolean;
  disableCalendar?: boolean;
  unavailable?: {
    /**
     * The specific dates that are unavailable. ISO 8601 format.
     * @example ["2025-07-02"]
     */
    dates?: string[];
    /**
     * The days of the week that are unavailable.
     * (1-7, where 1 is Monday and 7 is Sunday)
     */
    daysOfWeek?: (1 | 2 | 3 | 4 | 5 | 6 | 7)[];
  };
  minDate?: string;
  maxDate?: string;
  required?: boolean;
  /**
   * The IANA timezone to use for the calendar. Controls the meaning of the "Today"
   * button and which month/year is initially displayed. When the timezone differs
   * from the user's locale timezone, a disambiguation message is shown below the calendar.
   * @example "America/Los_Angeles"
   */
  timezone?: IanaZone;
} & DataTrackingId;

export type DateFieldSingleChange = {
  /**
   * The date in ISO 8601 format.
   * @example "2025-07-02"
   */
  date: string | null;
  /**
   * Whether the input is valid. That is, it matches the format and is not empty.
   * This does not mean that the date is valid according to the constraints.
   */
  isInputValid: boolean;
  /**
   * Whether the input is empty.
   */
  isInputEmpty: boolean;
  /**
   * Whether the date is valid according to the constraints.
   * Constraints include:
   * - Required
   * - Unavailable dates
   * - Unavailable days of the week
   * - Min date
   * - Max date
   */
  isDateValid: boolean;
};

export type DateFieldSingleChangeHandler = (
  change: DateFieldSingleChange,
) => void;

export const DateFieldSingle = (props: DateFieldSingleProps) => {
  const { aiMark, ...labelPropsWithoutAiMark } = props.labelProps || {};

  const data = {
    label: childrenToString(props.label),
    labelProps: labelPropsWithoutAiMark,
    description: childrenToString(props.description),
  };

  const trackingId = useTrackingId({
    name: "DateFieldSingle",
    data,
    hasOverride: !!props["data-tracking-id"],
  });

  const {
    onFocus,
    onBlur,
    disableCalendar,
    required,
    mode,
    value: valueProp,
    defaultValue: defaultValueProp,
    minDate: minDateProp,
    maxDate: maxDateProp,
    unavailable: unavailableProp,
    onChange: onChangeProp,
    label,
    timezone,
    ...rest
  } = props;

  const popoverRef = useRef<{
    openPopover: () => void;
    closePopover: () => void;
  }>(null);
  const inputRef = useRef<MaskedDateInputRef>(null);
  // We convert any props that deal in ISO 8601 strings to Luxon DateTime objects for easier internal use.
  const {
    value: normalizedValue,
    defaultValue: normalizedDefaultValue,
    minDate,
    maxDate,
    unavailable,
    onChange,
  } = useDateFieldSingleConversion({
    value: valueProp,
    defaultValue: defaultValueProp,
    onChange: onChangeProp,
    minDate: minDateProp,
    maxDate: maxDateProp,
    unavailable: unavailableProp,
  });

  const handleChange: DateFieldSingleStateChangeHandler = (change) => {
    onChange?.({
      ...change,
      isDateValid: validateDate({
        date: change.date,
        constraints: {
          required,
          unavailable,
          minDate: minDate ?? undefined,
          maxDate: maxDate ?? undefined,
        },
      }),
    });
  };

  // We use a hook to keep the value of input and the calendar in sync.
  const { value, handleInputChange, handleCalendarSelection } =
    useDateFieldSingleState({
      valueProp: normalizedValue,
      defaultValueProp: normalizedDefaultValue,
      onChange: handleChange,
    });

  const { props: wrapperProps } = useFocusWithin({
    onBlur: (e) => {
      onBlur?.(e);
      popoverRef.current?.closePopover();
    },
    onFocus,
  });

  const popoverContentRef = useRef<HTMLDivElement>(null);

  const currentValidity = useMemo(
    () =>
      validateDate({
        date: value,
        constraints: {
          required,
          unavailable,
          minDate: minDate ?? undefined,
          maxDate: maxDate ?? undefined,
        },
      }),
    [value, required, unavailable, minDate, maxDate],
  );

  const handleInputKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === "ArrowDown") {
      popoverRef.current?.openPopover();
      requestAnimationFrame(() => {
        popoverContentRef.current?.querySelector("button")?.focus();
      });
    }
  };

  const commonFieldProps = {
    ...rest,
    label,
    mode,
    onChange: handleInputChange,
    lastValidDate: value,
    required,
    autoComplete: "off",
    "data-date-valid": currentValidity,
    "data-tracking-id": trackingId,
    "aria-label": label ? undefined : "Select a Date",
  };
  if (disableCalendar) {
    return <MaskedDateInput ref={inputRef} {...commonFieldProps} />;
  }

  return (
    <div {...wrapperProps}>
      <Popover ref={popoverRef} placement="bottom-start" role="grid">
        <Popover.Trigger>
          {({ ref: iRef, onKeyDown }) => {
            const mergedRef = mergeRefs([
              iRef as Ref<HTMLInputElement>,
              inputRef,
            ]);
            return (
              <MaskedDateInput
                {...commonFieldProps}
                ref={mergedRef}
                onKeyDown={(ev) => {
                  onKeyDown?.(ev);
                  handleInputKeyDown(ev);
                }}
                onClick={() => popoverRef.current?.openPopover()}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            );
          }}
        </Popover.Trigger>
        <Popover.Content ref={popoverContentRef} data-testid="calendar-popover">
          <DateFieldSingleCalendar
            value={value}
            onSelection={(v) => {
              inputRef.current?.focus();
              handleCalendarSelection(v);
              popoverRef.current?.closePopover();
            }}
            minDate={minDate}
            maxDate={maxDate}
            unavailable={unavailable}
            timezone={timezone}
          />
        </Popover.Content>
      </Popover>
    </div>
  );
};

DateFieldSingle.displayName = "DateFieldSingle";
